import React from 'react';

import { Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useNotification } from 'segments/hooks';

import { InputStr, SubmiteButton } from 'shared/FormItems';
import { ModalFooter } from 'shared/Modals';

import { type Role } from 'features/user';
import { SelectUserRole, useGetUsersListQuery, useUpdateUserMutation } from 'features/users';
import './EditUserModal.scss';
import { selectActiveUserId } from 'features/users/store/usersSlice';

interface FormData {
  id: number;
  password?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: Role;
  banned?: boolean;
  note?: string;
}

interface Props {
  handleCancel: () => void;
}

export const EditUserModal = ({ handleCancel }: Props) => {
  const userId = useAppSelector(selectActiveUserId);
  const { t } = useTranslation(undefined, { keyPrefix: 'user' });

  const [form] = Form.useForm<FormData>();

  const { data = { items: [] } } = useGetUsersListQuery({ id: userId });
  const [updateUser, { isSuccess, isError, isLoading, error }] = useUpdateUserMutation();

  useNotification({
    isSuccess,
    isError,
    error,
    onSuccess: () => handleCancel(),
    onError: () => handleCancel(),
  });

  const userData = data.items[0];
  if (userData === undefined) {
    return;
  }

  const onFinish = (values: FormData) => {
    if (userData?.id !== undefined) {
      updateUser({
        id: userData.id,
        ...(values.email && values.email !== userData.email && { email: values.email }),
        ...(values.name && values.name !== userData.name && { name: values.name }),
        ...(values.phone && { phone: values.phone }),
        ...(values.password && { password: values.password }),
        ...(values.note && { note: values.note }),
        ...(values.role && values.role !== userData?.role && { role: values.role }),
      });
    }
  };

  return (
    <Modal
      title={t('edit-user-data', 'Редактирование данных пользователя')}
      open
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <ModalFooter
          key="modalFooter"
          handleCancel={handleCancel}
          resetFields={form.resetFields}
        />
      ]}
    >
      <Form
        name="basic"
        layout="vertical"
        form={form}
        initialValues={{ ...userData }}
        onFinish={onFinish}
        autoComplete="off"
        className="edit-user__form"
      >
        <InputStr type="name" />
        <InputStr type="email" />
        <InputStr type="phone" />
        <InputStr type="password" />
        <InputStr type="note" />
        <SelectUserRole />
        <SubmiteButton submiteText={t('edit-data', 'Редактировать данные')} isLoading={isLoading} />
      </Form>
    </Modal>
  );
};
